
import { Button, FormControlLabel, Menu, MenuItem, Paper, Select, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from '@mui/material'
import React, { useState } from 'react'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx';
import BootstrapTooltip from '../../../components/tooltip'
import { logoBase64 } from '../../../assets/images/dashboard/logo_base64'
import { leptonCheckLogoBase64 } from '../../../assets/images/dashboard/leptonCheck-logo_base64'

const EventsTab = ({ devices, userRoute, rows, selectStyles, tableCellHeaderStyle, tableCellBodyStyle, vehicleFilter, setVehicleFilter, checkpointFilter, setCheckpointFilter, statusFilter, setStatusFilter, handleSort, getSortIcon, getStatusStyle }) => {
  const [hideNullTurnNumber, setHideNullTurnNumber] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const filteredDevices = devices.filter(device =>
    rows.some(row => row.vehicle === device.label)
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newValue = parseInt(event.target.value, 10);

    // Validar que el nuevo valor esté dentro de las opciones disponibles
    if ([20, 30, 50].includes(newValue)) {
      setRowsPerPage(newValue);
      setPage(0);
    } else {
      console.warn(`Valor inválido para rowsPerPage: ${newValue}`);
    }
  };

  const filteredRows = rows.filter(row => {
    return (
      (vehicleFilter === '' || row.vehicle === vehicleFilter) &&
      (checkpointFilter === '' || row.checkpoint === checkpointFilter) &&
      (statusFilter === '' || row.status === statusFilter) &&
      (!hideNullTurnNumber || row.turnNumber !== '-') // Filtro del checkbox
    );
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClickDownload = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'A4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const headerHeight = 55;
    const logoWidth = 100;
    const logoHeight = 35;
    const marginX = 40;
    const footerMarginY = 18;
    const reportName = "Reporte de flotas por evento";


    // Guarda aquí los pies de página para editarlos después
    const footerTexts = [];

    autoTable(doc, {
      columns: [
        { header: 'Vehículo', dataKey: 'vehicle' },
        { header: 'Punto de Control', dataKey: 'checkpoint' },
        { header: 'Tiempo', dataKey: 'time' },
        { header: 'Vuelta', dataKey: 'turnNumber' },
        { header: 'Estatus', dataKey: 'status' },
        { header: 'Diferencia', dataKey: 'difference' },
        { header: 'Horario', dataKey: 'scheduleName' },
      ],
      body: filteredRows.map(row => ({
        vehicle: row.vehicle,
        checkpoint: row.checkpoint,
        time: row.time,
        turnNumber: row.turnNumber,
        status: row.status,
        difference: row.difference,
        scheduleName: row.scheduleName
      })),
      startY: headerHeight,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
      margin: { top: headerHeight },
      didDrawPage: function (data) {
        // --- Header ---
        if (logoBase64) {
          doc.addImage(logoBase64, 'PNG', marginX, 12, logoWidth, logoHeight);
        }

        // Dibuja el segundo logo (header derecho)
        const headerImgWidth = 165;  // ajusta según tamaño de tu nueva imagen
        const headerImgHeight = 35;  // ajusta según tamaño de tu nueva imagen
        if (leptonCheckLogoBase64) {
          doc.addImage(
            leptonCheckLogoBase64,
            'PNG',
            pageWidth - marginX - headerImgWidth,
            12,
            headerImgWidth,
            headerImgHeight
          );
        }

        // --- Footer marcador temporal ---
        const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
        footerTexts.push({
          page: pageNumber,
          y: pageHeight - footerMarginY,
        });

        // Pie de página: nombre del reporte (izquierda), paginación (derecha)
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(reportName, marginX, pageHeight - footerMarginY, { align: 'left' });
        doc.text(`Página ${pageNumber} de {total}`, pageWidth - marginX, pageHeight - footerMarginY, { align: 'right' });
      }
    });

    // --- Corrige el total de páginas en los pies ---
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      const footer = footerTexts[i - 1];
      if (footer) {
        // Borra área del paginado anterior (opcional, si quieres evitar solapamientos)
        doc.setFillColor(255, 255, 255);
        doc.rect(marginX - 5, footer.y - 10, pageWidth - 2 * marginX + 10, 16, "F");

        // Pie de página: nombre del reporte (izquierda), paginación (derecha)
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(reportName, marginX, footer.y, { align: 'left' });
        doc.text(`Página ${i} de ${totalPages}`, pageWidth - marginX, footer.y, { align: 'right' });
      }
    }

    doc.save('Reporte de flotas por eventos.pdf');
  };

  const handleExportExcel = () => {
    const data = [
      ['Vehículo', 'Punto de Control', 'Tiempo', 'Vuelta', 'Estatus', 'Diferencia', 'Horario'], // Encabezados
      ...filteredRows.map(row => [
        row.vehicle,
        row.checkpoint,
        row.time,
        row.turnNumber,
        row.status,
        row.difference,
        row.scheduleName
      ])
    ];

    // Crea la hoja
    const worksheet = XLSX.utils.aoa_to_sheet(data);

    // Ajuste de anchos de columna
    worksheet['!cols'] = [
      { wch: 10 },  // Vehículo
      { wch: 30 },  // Punto de Control
      { wch: 20 },  // Tiempo
      { wch: 10 },  // Vuelta
      { wch: 15 },  // Estatus
      { wch: 15 },  // Diferencia
      { wch: 20 }   // Horario
    ];

    // Aplica color a encabezado (solo algunos lectores Excel lo soportan)
    for (let c = 0; c < 7; c++) {
      const cellAddress = XLSX.utils.encode_cell({ c, r: 0 });
      worksheet[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "2980b9" } },
        alignment: { horizontal: "center", vertical: "center" }
      };
    }

    // Alterna color de filas (no todos los visores de Excel lo muestran, pero el dato queda)
    // Para compatibilidad avanzada usa xlsx-style o SheetJS Pro

    // Crea libro y descarga
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Reporte');
    XLSX.writeFile(workbook, 'Reporte de flotas por eventos.xlsx');
  };



  return (
    <div className='tab_content_container' >
      <div className='filters_container' style={{ marginBottom: '0.5rem' }}>
        <span>Filtrar por:</span>
        <Select
          id='generalVehicleFilter'
          value={vehicleFilter}
          onChange={(e) => setVehicleFilter(e.target.value)}
          displayEmpty
          sx={selectStyles}
        >
          <MenuItem value="" style={{ fontSize: '0.7rem' }}>Todos los vehículos</MenuItem>
          {filteredDevices.map(device => (
            <MenuItem key={device.id} value={device.label} style={{ fontSize: '0.7rem' }}>{device.label}</MenuItem>
          ))}
        </Select>
        <Select
          id='generalCheckpointFilter'
          value={checkpointFilter}
          onChange={(e) => setCheckpointFilter(e.target.value)}
          displayEmpty
          sx={selectStyles}
        >
          <MenuItem value="" style={{ fontSize: '0.7rem' }}>Todos los puntos de control</MenuItem>
          {userRoute.map(checkpoint => (
            <MenuItem key={checkpoint.id + checkpoint.label} value={checkpoint.label} style={{ fontSize: '0.7rem' }}>{checkpoint.label}</MenuItem>
          ))}
        </Select>
        <Select
          id='generalStatusFilter'
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          displayEmpty
          sx={selectStyles}
        >
          <MenuItem value="" style={{ fontSize: '0.7rem' }}>Todos los estatus</MenuItem>
          <MenuItem value="Atrasado" style={{ fontSize: '0.7rem' }}>Atrasado</MenuItem>
          <MenuItem value="En tiempo" style={{ fontSize: '0.7rem' }}>En tiempo</MenuItem>
          <MenuItem value="Adelantado" style={{ fontSize: '0.7rem' }}>Adelantado</MenuItem>
        </Select>
        <FormControlLabel
          control={
            <Switch
              checked={hideNullTurnNumber}
              onChange={(e) => setHideNullTurnNumber(e.target.checked)}
              color="primary"
            />
          }
          label={"Ocultar valores nulos"}
          labelPlacement="start"
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginLeft: 'auto' }}>
          <BootstrapTooltip title="Descargar">
            <Button
              variant="contained"
              color="success"
              sx={{
                padding: '0 0.8rem',
                fontSize: '0.7rem',
                color: 'white',
                borderRadius: 'var(--border-radius-1)',
                transition: 'all 0.3s ease',
                height: '2rem',
                minWidth: 'auto'
              }}
              onClick={handleClickDownload}
            >
              <span className="material-symbols-rounded" style={{ verticalAlign: 'middle', fontSize: '1rem' }}>download</span>
            </Button>
          </BootstrapTooltip>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              sx={{ fontSize: '0.8rem' }}
              onClick={() => { handleExportPDF(); handleClose(); }}
            >
              Formato PDF
            </MenuItem>
            <MenuItem
              sx={{ fontSize: '0.8rem' }}
              onClick={() => { handleExportExcel(); handleClose(); }}
            >
              Formato XLSX
            </MenuItem>
          </Menu>
        </div>


      </div>
      <TableContainer component={Paper} sx={{ backgroundColor: 'transparent', borderRadius: 'var(--card-border-radius)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('vehicle')}><span>Vehículo</span>{getSortIcon('vehicle')}</TableCell>
              <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('checkpoint')}><span>Punto de Control</span>{getSortIcon('checkpoint')}</TableCell>
              <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('time')}><span>Tiempo</span>{getSortIcon('time')}</TableCell>
              <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('turnNumber')}><span>Vuelta</span>{getSortIcon('turnNumber')}</TableCell>
              <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('status')}><span>Estatus</span>{getSortIcon('status')}</TableCell>
              <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('difference')}><span>Diferencia</span>{getSortIcon('difference')}</TableCell>
              <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('scheduleName')}><span>Horario</span>{getSortIcon('scheduleName')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
              <TableRow key={index} sx={{ height: '2.3rem' }}>
                <TableCell sx={tableCellBodyStyle}>{row.vehicle}</TableCell>
                <TableCell sx={tableCellBodyStyle}>{row.checkpoint}</TableCell>
                <TableCell sx={tableCellBodyStyle}>{row.time}</TableCell>
                <TableCell sx={tableCellBodyStyle}>{row.turnNumber}</TableCell>
                <TableCell sx={tableCellBodyStyle}><span className={`table_status_value ${row.status === '-' ? null : ''}`} style={getStatusStyle(row.status)}>{row.status}</span></TableCell>
                <TableCell sx={tableCellBodyStyle}>{row.difference}</TableCell>
                <TableCell sx={tableCellBodyStyle}>{row.scheduleName}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[20, 30, 50]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página" // Traducción al español
          sx={{
            backgroundColor: 'var(--color-white)',
            color: 'var(--color-dark)',
            fontSize: '0.7rem',
            '& .MuiTablePagination-selectLabel': {
              color: 'var(--color-dark)',
              fontSize: '0.7rem',
            },
            '& .MuiTablePagination-input': {
              color: 'var(--color-dark)',
              fontSize: '0.7rem',
            },
            '& .MuiSelect-icon': {
              color: 'var(--color-dark)',
              fontSize: '1.2rem',
            },
            '& .MuiTablePagination-actions': {
              color: 'var(--color-dark)',
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: '0.7rem',
            }
          }}
          slotProps={{
            select: {
              sx: {
                fontSize: '0.7rem', // Ajustar tamaño del texto dentro del selector
              },
              MenuProps: {
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      fontSize: '0.7rem', // Cambia el tamaño de las opciones en el menú desplegable
                    }
                  }
                }
              }
            }
          }}
        />

      </TableContainer>
    </div>
  )
}

export default EventsTab